/**
 * Locations Management (master data).
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-22 The form edits Latitude / Longitude (numbers, both or neither, in range) — the
 *          columns LexiStar and geofencing read — instead of the free-text "Geographic
 *          Coordinates" nothing read.
 *   ORD-12 Add / Edit / Delete / the active toggle only for roles that may manage orders;
 *          a failed save shows the server's reason in the form.
 */

import { useState, useEffect, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Search, X, Check, Loader, Edit2, Trash2, ArrowLeft } from 'lucide-react';
import orderService from '../../../../services/orderService';
import { TableSkeleton, EmptyState, InlineAlert } from '../shared';
import { useAuth } from '../../../../types/commonComponents';
import { apiErrorMessage, ORDERS_MANAGE, parseLatLon } from '../shared/orderHelpers';
import type { LocationPayload, LocationRow } from '../../../../types/clientPages/orderManagement';

const FONT = { fontFamily: 'DM Sans, sans-serif' };
const MONO = { fontFamily: 'IBM Plex Mono, monospace' };

interface TypeStyle {
  short: string;
  bg: string;
  text: string;
  border: string;
  dot: string;
}

const TYPE_CFG: Record<string, TypeStyle | undefined> & { Both: TypeStyle } = {
  'Dispatch Point': {
    short: 'Dispatch',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
    dot: 'bg-sky-500',
  },
  'Receipt Point': {
    short: 'Receipt',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
  },
  Both: {
    short: 'Both',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
  },
};

const TypeBadge = ({ type }: { type: string }) => {
  const c = TYPE_CFG[type] || TYPE_CFG['Both'];
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2 py-0.5 text-[10px] font-semibold rounded-full border ${c.bg} ${c.text} ${c.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.short}
    </span>
  );
};

const Toggle = ({
  on,
  onClick,
  title,
  label,
}: {
  on: boolean;
  onClick: () => void;
  title?: string;
  label?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label || title}
    onClick={onClick}
    className={`relative inline-flex items-center rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}
    style={{ width: 36, height: 20 }}
    title={title}
  >
    <span
      className={`inline-block w-4 h-4 rounded-full bg-surface shadow transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-[2px]'}`}
    />
  </button>
);

/** The form's text state; latitude / longitude stay text until they are checked. */
interface LocationForm {
  location_code: string;
  location_name: string;
  location_type: string;
  address: string;
  city: string;
  state_province: string;
  country: string;
  postal_code: string;
  latitude: string;
  longitude: string;
  active_status: boolean;
}

type TextField = Exclude<keyof LocationForm, 'active_status'>;
type LocationFormErrors = Partial<Record<keyof LocationForm, string>>;

const coord = (v: number | null | undefined): string => (v == null ? '' : String(v));

const INPUT_CLASS =
  'w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';
const LABEL_CLASS =
  'block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5';

interface LocationModalProps {
  location: LocationRow | null;
  onSave: () => void;
  onCancel: () => void;
}

const LocationModal = ({ location, onSave, onCancel }: LocationModalProps) => {
  const [form, setForm] = useState<LocationForm>(() => ({
    location_code: location?.location_code || '',
    location_name: location?.location_name || '',
    location_type: location?.location_type || 'Both',
    address: location?.address || '',
    city: location?.city || '',
    state_province: location?.state_province || '',
    country: location?.country || '',
    postal_code: location?.postal_code || '',
    latitude: coord(location?.latitude),
    longitude: coord(location?.longitude),
    active_status: location?.active_status ?? true,
  }));
  const [errors, setErrors] = useState<LocationFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof LocationForm>(f: K, v: LocationForm[K]) => {
    setForm((p) => ({ ...p, [f]: v }));
    setErrors((p) => ({ ...p, [f]: '' }));
  };

  const handleSubmit = async () => {
    const e: LocationFormErrors = {};
    if (!form.location_code) e.location_code = 'Required';
    if (!form.location_name) e.location_name = 'Required';
    if (!form.location_type) e.location_type = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;
    const point = parseLatLon(form.latitude, form.longitude);
    if (!point.ok) {
      setSaveError(point.error);
      return;
    }
    const payload: LocationPayload = {
      location_code: form.location_code,
      location_name: form.location_name,
      location_type: form.location_type,
      address: form.address,
      city: form.city,
      state_province: form.state_province,
      country: form.country,
      postal_code: form.postal_code,
      latitude: point.latitude,
      longitude: point.longitude,
      active_status: form.active_status,
    };
    setSaveError(null);
    try {
      setSaving(true);
      if (location) {
        await orderService.updateLocation(location.location_id, payload);
      } else {
        await orderService.createLocation(payload);
      }
      onSave();
    } catch (err) {
      console.error(err);
      const timedOut = (err as { code?: string } | null)?.code === 'ECONNABORTED';
      setSaveError(
        timedOut
          ? 'Request timed out — please try again.'
          : apiErrorMessage(err, 'Failed to save location')
      );
    } finally {
      setSaving(false);
    }
  };

  const textInput = (field: TextField, label: string, placeholder: string, required = false) => (
    <div key={field}>
      <label htmlFor={`location-${field}`} className={LABEL_CLASS}>
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        id={`location-${field}`}
        type="text"
        value={form[field]}
        onChange={(ev) => set(field, ev.target.value)}
        className={`${INPUT_CLASS} ${errors[field] ? 'border-red-400' : 'border-slate-200'}`}
        placeholder={placeholder}
      />
      {errors[field] && <p className="text-red-500 text-xs mt-1 font-medium">{errors[field]}</p>}
    </div>
  );

  const title = location ? 'Edit Location' : 'New Location';
  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-modal-title"
        className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp"
        onClick={(e: MouseEvent) => e.stopPropagation()}
        style={FONT}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-600" />
            </div>
            <h3 id="location-modal-title" className="text-base font-bold text-slate-800">
              {title}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {textInput('location_code', 'Location Code', 'e.g. LOC-001', true)}
            {textInput('location_name', 'Location Name', 'e.g. Main Warehouse', true)}
          </div>

          <fieldset>
            <legend className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Location Type <span className="text-red-400">*</span>
            </legend>
            <div className="flex flex-wrap gap-3">
              {['Dispatch Point', 'Receipt Point', 'Both'].map((t) => (
                <label key={t} className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="location_type"
                    value={t}
                    checked={form.location_type === t}
                    onChange={(ev) => set('location_type', ev.target.value)}
                    className="w-4 h-4 text-teal-600 border-slate-300 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
                    {t}
                  </span>
                </label>
              ))}
            </div>
            {errors.location_type && (
              <p className="text-red-500 text-xs mt-1 font-medium">{errors.location_type}</p>
            )}
          </fieldset>

          {textInput('address', 'Address', 'Street address')}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {textInput('city', 'City', 'City')}
            {textInput('state_province', 'State/Province', 'State/Province')}
            {textInput('country', 'Country', 'Country')}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {textInput('postal_code', 'Postal Code', 'e.g. 2000')}
            {(
              [
                ['latitude', 'Latitude', 'e.g. -26.2041', -90, 90],
                ['longitude', 'Longitude', 'e.g. 28.0473', -180, 180],
              ] as const
            ).map(([field, label, ph, min, max]) => (
              <div key={field}>
                <label htmlFor={`location-${field}`} className={LABEL_CLASS}>
                  {label}
                </label>
                <input
                  id={`location-${field}`}
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min={min}
                  max={max}
                  value={form[field]}
                  onChange={(ev) => set(field, ev.target.value)}
                  className={`${INPUT_CLASS} border-slate-200`}
                  placeholder={ph}
                  style={MONO}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 -mt-2">
            Decimal degrees. LexiStar and geofencing place the site at this point; leave both empty
            if it is not known.
          </p>

          <div className="flex items-center justify-between py-2 border-t border-slate-100">
            <div>
              <p className="text-sm font-semibold text-slate-700">Active Location</p>
              <p className="text-xs text-slate-400">
                Inactive locations won&apos;t appear in order forms
              </p>
            </div>
            <Toggle
              on={form.active_status}
              onClick={() => set('active_status', !form.active_status)}
              label="Active Location"
            />
          </div>

          {saveError && <InlineAlert>{saveError}</InlineAlert>}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-40 transition-colors text-sm font-semibold flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {location ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div
    className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={onCancel}
  >
    <div
      className="bg-surface rounded-xl shadow-2xl max-w-sm w-full p-5 animate-slideUp"
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-4 h-4 text-amber-500" />
        </div>
        <p className="text-sm text-slate-700 font-medium">{message}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600"
        >
          Deactivate
        </button>
      </div>
    </div>
  </div>
);

/* ── Mobile Card ───────────────────────────── */
interface LocationCardProps {
  location: LocationRow;
  canManage: boolean;
  onEdit: (location: LocationRow) => void;
  onDelete: (location: LocationRow) => void;
  onToggle: (location: LocationRow) => void;
}

const LocationCard = ({ location, canManage, onEdit, onDelete, onToggle }: LocationCardProps) => (
  <div
    className={`bg-surface rounded-xl border border-slate-200 p-4 transition-colors ${!location.active_status ? 'opacity-50' : ''}`}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 truncate">{location.location_name}</p>
        <p className="text-[11px] text-slate-400 font-medium truncate" style={MONO}>
          {location.location_code}
        </p>
      </div>
      <TypeBadge type={location.location_type} />
    </div>
    {location.city && <p className="text-xs text-slate-500 mb-3">{location.city}</p>}
    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
      {canManage ? (
        <Toggle
          on={location.active_status}
          onClick={() => onToggle(location)}
          title={location.active_status ? 'Active' : 'Inactive'}
          label={`${location.location_name} active`}
        />
      ) : (
        <span className="text-xs text-slate-500">
          {location.active_status ? 'Active' : 'Inactive'}
        </span>
      )}
      {canManage && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(location)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
            title="Edit location"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(location)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete location"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  </div>
);

const TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Dispatch', value: 'Dispatch Point' },
  { label: 'Receipt', value: 'Receipt Point' },
  { label: 'Both', value: 'Both' },
];

const TH = 'px-4 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-widest';

/* ═══════════════════════════════════════════════ */
const Locations = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<LocationRow | null>(null);
  const [deleting, setDeleting] = useState<LocationRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await orderService.getLocations();
      setLocations(d.locations || []);
    } catch (e) {
      console.error(e);
      setActionError(apiErrorMessage(e, 'Failed to load the locations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = search.toLowerCase();
  const filtered = locations.filter(
    (l) =>
      (!q ||
        (l.location_code || '').toLowerCase().includes(q) ||
        (l.location_name || '').toLowerCase().includes(q) ||
        (l.city || '').toLowerCase().includes(q)) &&
      (!typeFilter || l.location_type === typeFilter)
  );

  const handleToggleActive = async (loc: LocationRow) => {
    setActionError(null);
    try {
      await orderService.updateLocation(loc.location_id, { active_status: !loc.active_status });
      load();
    } catch (e) {
      console.error(e);
      setActionError(apiErrorMessage(e, 'Failed to change the location status'));
    }
  };

  const handleDelete = async (loc: LocationRow) => {
    setActionError(null);
    try {
      await orderService.deleteLocation(loc.location_id);
      load();
    } catch (e) {
      setActionError(apiErrorMessage(e, 'Failed to deactivate the location'));
    }
    setDeleting(null);
  };

  const openEdit = (loc: LocationRow | null) => {
    setEditing(loc);
    setShowModal(true);
  };

  const typeCounts: Record<string, number> = {
    '': locations.length,
    'Dispatch Point': locations.filter((l) => l.location_type === 'Dispatch Point').length,
    'Receipt Point': locations.filter((l) => l.location_type === 'Receipt Point').length,
    Both: locations.filter((l) => l.location_type === 'Both').length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" style={FONT}>
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/settings/modules/order-management/master-data')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-5 group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        Master Data
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center shadow-sm flex-shrink-0">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">Locations</h1>
              <span
                className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full"
                style={MONO}
              >
                {filtered.length}
              </span>
            </div>
            <p className="text-sm text-slate-500">Manage dispatch and receipt points</p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => openEdit(null)}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center text-sm font-semibold shadow-sm gap-2 self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" /> Add Location
          </button>
        )}
      </div>

      {actionError && (
        <InlineAlert className="mb-4" onDismiss={() => setActionError(null)}>
          {actionError}
        </InlineAlert>
      )}

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-slate-200 p-3 mb-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTypeFilter(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  typeFilter === opt.value
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {opt.label}
                <span
                  className={`ml-1.5 tabular-nums ${typeFilter === opt.value ? 'text-teal-200 dark:text-teal-900' : 'text-slate-400'}`}
                  style={MONO}
                >
                  {typeCounts[opt.value]}
                </span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, name, or city..."
              aria-label="Search locations"
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : filtered.length === 0 ? (
        <div className="bg-surface rounded-xl border border-slate-200">
          <EmptyState
            icon={MapPin}
            title="No locations found"
            description={
              search || typeFilter ? 'Try adjusting your filters' : 'No locations set up yet'
            }
          />
        </div>
      ) : (
        <>
          {/* ── Desktop Table (hidden below md) ── */}
          <div className="hidden md:block bg-surface rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/60">
                    <th className={TH}>Code</th>
                    <th className={TH}>Name</th>
                    <th className={TH}>Type</th>
                    <th className={`${TH} hidden lg:table-cell`}>City</th>
                    <th className={TH}>Status</th>
                    {canManage && <th className={`${TH} text-right`} />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((l) => (
                    <tr
                      key={l.location_id}
                      className={`hover:bg-slate-50 transition-colors ${!l.active_status ? 'opacity-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <span
                          className="text-sm font-bold text-slate-800 whitespace-nowrap"
                          style={MONO}
                        >
                          {l.location_code}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-800 font-medium">{l.location_name}</div>
                        {l.address && (
                          <div className="text-xs text-slate-400 truncate max-w-xs">
                            {l.address}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <TypeBadge type={l.location_type} />
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 hidden lg:table-cell">
                        {l.city || <span className="text-slate-300">&mdash;</span>}
                      </td>
                      <td className="px-4 py-3">
                        {canManage ? (
                          <Toggle
                            on={l.active_status}
                            onClick={() => handleToggleActive(l)}
                            title={l.active_status ? 'Active' : 'Inactive'}
                            label={`${l.location_name} active`}
                          />
                        ) : (
                          <span className="text-xs text-slate-500">
                            {l.active_status ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(l)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                              title="Edit location"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleting(l)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete location"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile Cards (hidden at md+) ── */}
          <div className="md:hidden grid grid-cols-1 gap-3">
            {filtered.map((l) => (
              <LocationCard
                key={l.location_id}
                location={l}
                canManage={canManage}
                onEdit={openEdit}
                onDelete={(loc) => setDeleting(loc)}
                onToggle={handleToggleActive}
              />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {showModal && (
        <LocationModal
          location={editing}
          onSave={() => {
            setShowModal(false);
            setEditing(null);
            load();
          }}
          onCancel={() => {
            setShowModal(false);
            setEditing(null);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          message={`Deactivate "${deleting.location_name}"? It stays in the list as inactive and can be switched back on; order forms no longer offer it.`}
          onConfirm={() => handleDelete(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

export default Locations;
